import dayjs from 'dayjs';
import { assign } from '@atds-legacy/lang';
import throttle from 'lodash/throttle';
import * as _dom from '@atds-legacy/dom';
import time from '@/shared/libs/time';
import ModelLocal from '@/shared/libs/model-local';
import dragScroll from './drag-scroll';
import DomEvent from './dom-event';

const MOVE_EVENT_THROTTLE = 300; // [ms] value is adjusted to performance
const AMOUNT_OF_EVENT_EMITTER_LISTENERS = 2000;
const CHANNEL_VISIBILITY_OFFSET = 3;
const POSITION_CHANGE_THRESHOLD = 300;
const HORIZONTAL_SCROLL_DURATION = 500;

// TODO refactor this class
export default class EpgDom extends ModelLocal {
  constructor(container, options = {}) {
    super({
      viewport: {
        top: undefined,
        left: undefined,
        fromPeriod: undefined,
        toPeriod: undefined,
        fromChannelSet: undefined,
        toChannelSet: undefined,
      },
    }, { namespace: 'epg-grid' });

    // values not saved in local storage
    assign(this, {
      container,
      nav: container.querySelector('.js-epg-nav'),
      holder: container.querySelector('.js-epg-grid-holder'),
      grid: container.querySelector('.js-epg-grid-wrapper'),
      canvas: container.querySelector('.js-epg-grid'),
      time: container.querySelector('.js-epg-time'),
      scroll: container.querySelector('.js-epg-timeline-scroll'),
      scrollDrag: container.querySelector('.js-epg-timeline-scroll-drag'),
      timeline: undefined,
      channels: container.querySelector('.js-epg-channels'),
      channelHeight: 96,
      hoursInDay: this.getHoursInEpgDay(),
      channelsInChunk: 200,
      marker: 0,
      hoursPerCanvas: 3,
      isInLiveRange: true,
    }, options);

    this.onScrollRef = throttle(() => this.onScroll(), 100);
    this.onResizeRef = throttle(() => this.onResize(), 250);

    this.translatePosition = 0;
    this.mask = {};

    this.dragPosition = 0;

    this.chunkLoaders = [];
    for (let i = 0; i < this.hoursInDay; i++) {
      this.chunkLoaders.push(i);
    }

    this.setMaxListeners(AMOUNT_OF_EVENT_EMITTER_LISTENERS);

    window.addEventListener('resize', this.onResizeRef);
    window.addEventListener('scroll', this.onScrollRef);
    this.on(DomEvent.SYNTHETIC_MOVE, throttle(() => this.emit(DomEvent.MOVE), MOVE_EVENT_THROTTLE));
  }

  /**
   * Setup initial state:
   * - calculate dimensions
   * - scroll to saved position or current time
   * - bind scroll events
   *
   * @param channels
   * @returns {Promise<EpgDom>}
   */
  setup(channels) {
    // TODO fetch and entire model class is unneeded
    return this.fetch().then(() => {
      this.hoursPerCanvas = this.$window.isBelowHdView ? 2 : 3;
      this.calculate(channels);

      this.scrollToMarker();
      this.updateViewport();

      setTimeout(() => this.bind(), 0);
      return this;
    });
  }

  bind() {
    this.initSlider();
    this.initDrag();

    dragScroll.call(this, window, {
      axis: 'y',
      dragElement: this.canvas.parentNode,
      moveEventName: DomEvent.SYNTHETIC_MOVE,
    });
  }

  onResize() {
    this.hoursPerCanvas = this.$window.isBelowHdView ? 2 : 3;
    this.calculate();
    this.scrollTo((this.canvasMaxWidth * this.percentPosition) / 100);
    this.emit(DomEvent.SYNTHETIC_MOVE);
  }

  onScroll() {
    this.calculate();
    this.updateViewport();
    this.emit(DomEvent.SYNTHETIC_MOVE);
  }

  onEpgDayChange() {
    const hoursInDay = this.getHoursInEpgDay();

    if (this.hoursInDay !== hoursInDay) {
      this.hoursInDay = hoursInDay;
      this.onResize();
    }
  }

  initDrag() {
    let initPosition = 0;

    const onMouseDown = (event) => {
      event.preventDefault();

      initPosition = event.clientX + parseInt(this.translatePosition, 10);

      document.addEventListener('mouseup', onMouseUp);
      document.addEventListener('mousemove', onMouseMove);
      this.emit(DomEvent.DRAG_START);
    };
    const onMouseMove = (event) => {
      event.preventDefault();

      let position = event.clientX - initPosition;
      if (position > 0) {
        position = 0;
      }

      if (Math.abs(position) > (this.canvasMaxWidth)) {
        position = (this.canvasMaxWidth);
      }

      this.scrollTo(Math.abs(position));
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      this.emit(DomEvent.DRAG_STOP);
    };

    this.canvas.parentNode.addEventListener('mousedown', onMouseDown);
  }

  initSlider() {
    const onMouseDown = (event) => {
      update(event);

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (event) => {
      update(event);
      this.emit(DomEvent.SYNTHETIC_MOVE);
    };

    const update = (event) => {
      event.preventDefault();
      const position = event.clientX - _dom.getAbsolutePosition(this.scroll).left;
      const percentPosition = ((position - (this.scrollDrag.offsetWidth / 2)) / (this.scroll.offsetWidth - this.scrollDrag.offsetWidth)) * 100;
      this.scrollTo((this.canvasMaxWidth * percentPosition) / 100);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);

      if (this.dragPosition < 0) {
        this.scrollDrag.style.marginLeft = 0;

        if (this.dragPosition < -20) {
          this.emit('prev');
        }
      }

      const maxRightPosition = this.scroll.offsetWidth - this.scrollDrag.offsetWidth;
      if (this.dragPosition > maxRightPosition) {
        this.scrollDrag.style.marginLeft = (this.scroll.offsetWidth - this.scrollDrag.offsetWidth) + 'px';

        if (this.dragPosition - maxRightPosition > 20) {
          this.emit('next');
        }
      }

      this.emit(DomEvent.SYNTHETIC_MOVE);
    };

    this.scroll.addEventListener('mousedown', onMouseDown);
  }

  scrollTo(position, duration = 0) {
    const scrollWidth = this.scroll.offsetWidth;

    this.percentPosition = ((position / this.canvasMaxWidth) * 100);
    this.dragPosition = (((scrollWidth - this.scrollDrag.offsetWidth) * this.percentPosition) / 100);

    if (this.dragPosition < 0) {
      this.dragPosition = this.dragPosition / 4;
    }

    if (this.dragPosition < -30) {
      document.dispatchEvent(new CustomEvent('mouseup'));
      return;
    }

    if (this.dragPosition > scrollWidth - this.scrollDrag.offsetWidth) {
      const maxRightPosition = scrollWidth - this.scrollDrag.offsetWidth;
      const rightDragPosition = (this.dragPosition - maxRightPosition) / 4;
      this.dragPosition = maxRightPosition + rightDragPosition;

      if (rightDragPosition > 40) {
        document.dispatchEvent(new CustomEvent('mouseup'));
        return;
      }
    }

    this.scrollDrag.style.marginLeft = this.dragPosition + 'px';

    if (position <= 0) {
      position = 0;
      this.emit(DomEvent.LEFT_EDGE_REACHED);
    } else if (position >= this.canvasMaxWidth) {
      position = this.canvasMaxWidth;
      this.emit(DomEvent.RIGHT_EDGE_REACHED);
    } else {
      this.emit(DomEvent.NO_EDGE_REACHED);
    }

    this.translatePosition = position;

    this.timeline.style['transition-duration'] = duration + 'ms';
    this.canvas.style['transition-duration'] = duration + 'ms';
    this.canvas.style.transform = 'translateX(' + (-position) + 'px)';
    this.timeline.style.transform = 'translateX(' + (-position) + 'px)';

    if (duration > 0) {
      this.canvas.style['transition-timing-function: ease'] = 'ease';
      this.timeline.style['transition-timing-function: ease'] = 'ease';
    }

    this.updateMarkerPosition();
    this.updateMask();
  }

  updateMarkerPosition() {
    const position = this.translatePosition || 0;
    const start = ((position / this.secondWidth) * 1000) + time.today.clone().startOf('day').valueOf();
    const stop = start + ((this.canvasWidth / this.secondWidth) * 1000);

    if (start > time.now.valueOf()) {
      this.isInLiveRange = false;
      this.container.classList.add('marker-behind');
      this.container.classList.remove('marker-ahead');
      this.marker = position;
    } else if (stop < time.now.valueOf()) {
      this.isInLiveRange = false;
      this.container.classList.remove('marker-behind');
      this.container.classList.add('marker-ahead');
      this.marker = position + this.canvasWidth;
    } else {
      this.isInLiveRange = true;
      this.container.classList.remove('marker-behind');
      this.container.classList.remove('marker-ahead');
      this.updateMarker();
    }
  }

  updateMask() {
    const start = ((this.translatePosition / this.secondWidth) * 1000) + time.today.clone().startOf('day').valueOf();
    const stop = start + ((this.canvasWidth / this.secondWidth) * 1000);
    const navRect = this.nav.getBoundingClientRect();
    const gridTop = navRect.height;
    const top = this.channels.getBoundingClientRect().top;
    const _topIndex = Math.max(Math.floor((gridTop - top) / this.channelHeight), 0);
    const bottomIndex = _topIndex + Math.floor((this.visibleHeight - gridTop) / this.channelHeight) + CHANNEL_VISIBILITY_OFFSET;
    const topIndex = Math.max(_topIndex - CHANNEL_VISIBILITY_OFFSET, 0);
    this.mask = { start, stop, topIndex, bottomIndex };

    return this.mask;
  }

  unbind() {
    window.removeEventListener('resize', this.onResizeRef);
    window.removeEventListener('scroll', this.onScrollRef);
  }

  isVisible() {
    const elem = this.container;
    return !!(elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length);
  }

  calculate() {
    if (!this.isVisible()) {
      return false;
    }

    this.channelsWidth = _dom.getWidth(this.channels);
    this.canvasWidth = _dom.getWidth(this.canvas);

    this.hourWidth = this.canvasWidth / this.hoursPerCanvas;
    this.minuteWidth = this.hourWidth / 60;
    this.secondWidth = this.minuteWidth / 60;
    this.canvasMaxWidth = (this.hoursInDay * this.hourWidth) - this.canvasWidth;

    this.gridWidth = _dom.getWidth(this.grid);
    this.visibleWidth = this.gridWidth - this.channelsWidth;
    this.visibleHeight = _dom.getHeight(this.grid);

    this.updateMask();

    return true;
  }

  updateViewport() {
    const left = _dom.scrollLeft(this.grid);
    const top = _dom.scrollTop(this.grid);
    const right = left + this.visibleWidth;
    const bottom = top + this.visibleHeight;
    const fromPeriod = this.getPeriodIndex(left);
    const toPeriod = this.getPeriodIndex(right);
    const fromChannelSet = this.getChannelSet(top);
    const toChannelSet = this.getChannelSet(bottom);

    if (fromPeriod === this.viewport.fromPeriod
      && toPeriod === this.viewport.toPeriod
      && fromChannelSet === this.viewport.fromChannelSet
      && toChannelSet === this.viewport.toChannelSet) {
      return false;
    }

    this.viewport = {
      top,
      left,
      fromPeriod,
      toPeriod,
      fromChannelSet,
      toChannelSet,
    };

    this.emit('viewport', this.viewport);
    this.updateMask();

    return true;
  }

  dateToOffset(date) {
    if (typeof date === 'string') {
      date = dayjs(date);
    }

    const day = time.today;
    let diff = date.diff(day, 'seconds');

    if (diff < 0) {
      diff = 0;
    }

    return Math.floor(diff * this.secondWidth);
  }

  getPeriodIndex(horizontalPosition) {
    const period = Math.floor(horizontalPosition / this.hourWidth / this.hoursInDay);
    const maxPeriod = Math.round(24 / this.hoursInDay) - 1;

    return period > maxPeriod ? maxPeriod : period;
  }

  getChannelSet(verticalPos) {
    return Math.floor(verticalPos / this.channelHeight / this.channelsInChunk);
  }

  getChunkLeftOffset(period) {
    return period * this.hoursInDay * this.hourWidth;
  }

  scrollLeft(left, duration) {
    this.scrollTo(left, duration);
  }

  updateMarker() {
    const offsetMarker = time.now.diff(time.now.clone().startOf('day'), 'seconds');
    if (!this.container.classList.contains('marker-ahead') && !this.container.classList.contains('marker-ahead')) {
      this.marker = Math.floor(offsetMarker * this.secondWidth) || undefined;
    }
  }

  scrollToMarker() {
    this.updateMarker();
    const nowToHour = time.now.clone().startOf('hour').subtract(15, 'minutes');
    const offsetWindow = nowToHour.diff(time.now.clone().startOf('day'), 'seconds');
    this.scrollLeft(Math.floor(offsetWindow * this.secondWidth), 200);
    this.emit(DomEvent.MOVE);
  }

  horizontalScrollLeft() {
    this.scrollTo(this.translatePosition - POSITION_CHANGE_THRESHOLD, HORIZONTAL_SCROLL_DURATION);
    this.emit(DomEvent.SYNTHETIC_MOVE);
  }

  horizontalScrollRight() {
    this.scrollTo(this.translatePosition + POSITION_CHANGE_THRESHOLD, HORIZONTAL_SCROLL_DURATION);
    this.emit(DomEvent.SYNTHETIC_MOVE);
  }

  getHoursInEpgDay() {
    return Math.round(time.today.endOf('day').diff(time.today.startOf('day'), 'h', true));
  }

  get position() {
    return {
      top: _dom.scrollTop(this.grid),
      left: _dom.scrollLeft(this.grid),
    };
  }
}
